import { useState, useEffect, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../../components/CardV2/CardV2';
import LinearGraph from '../../../components/LinearGraph/LinearGraph';
import NoData from '../../../components/NoData/NoData';

type TimeRange = { start: string; end: string };

type ChartCardProps = {
  timeRange: TimeRange;
  title: string;
  icon?: ReactNode;
  apiCall: (params: { startTime: string; endTime: string }) => Promise<{ data?: { labels?: string[]; values?: number[] } }>;
  dataLabel: string;
  backgroundColor: string;
  borderColor: string;
};

const ChartCard = ({ 
  timeRange, 
  title, 
  icon = <TrendingUp className="h-5 w-5" />, 
  apiCall, 
  dataLabel, 
  backgroundColor, 
  borderColor 
}: ChartCardProps) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<{ label: string; data: number[]; backgroundColor: string; borderColor: string; borderWidth: number }[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const apiCallRef = useRef(apiCall);

  useEffect(() => {
    apiCallRef.current = apiCall;
  }, [apiCall]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    apiCallRef.current({ 
      startTime: timeRange.start, 
      endTime: timeRange.end 
    })
      .then((res) => {
        if (!mounted) return;
        const data = res.data;
        if (data?.labels && data?.values) {
          setLabels(data.labels);
          setChartData([{
            label: dataLabel,
            data: data.values,
            backgroundColor,
            borderColor,
            borderWidth: 2,
          }]);
        } else {
          setLabels([]);
          setChartData([]);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setLabels([]);
        setChartData([]);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => { mounted = false };
  }, [timeRange.start, timeRange.end, dataLabel, backgroundColor, borderColor]);
  
  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">{t('global.loading')}</div>
          </div>
        ) : chartData.length > 0 && labels.length > 0 ? (
          <LinearGraph 
            title={title} 
            xLabels={labels} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message={t('stats.commission_contracts.no_data_available')} />
        )}
      </CardContent>
    </CardV2>
  );
};

export default ChartCard;

